import dayjs, { Dayjs } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import en from 'dayjs/locale/en';

dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isLeapYear);
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

// set dayjs to start the week from Monday
dayjs.locale({ ...en, weekStart: 1 }, undefined, true);

/**
 * A utility class encapsulating common date and time operations using Day.js.
 * This provides a consistent interface across the application and makes library replacement easier.
 */
class DateTime {
  /** To dayjs */
  static toDayjs(date: dayjs.ConfigType): dayjs.Dayjs {
    return dayjs(date);
  }

  /** Returns the current date and time. */
  static now() {
    return dayjs();
  }

  /** Returns next date from today */
  static tomorrow() {
    return dayjs().add(1, 'day');
  }

  /** Returns the previous date from today */
  static yesterday() {
    return dayjs().subtract(1, 'day');
  }

  /** Formats a date into the given string format. */
  static format(date: dayjs.ConfigType, format: string = 'DD MMM, YYYY') {
    return dayjs(date).format(format);
  }

  /** Formats a date if valid, otherwise returns the input as string. */
  static formatIfValid(
    dateInput: unknown,
    format: string = 'DD MMM, YYYY',
  ): string {
    const d = dayjs(dateInput as dayjs.ConfigType);

    if (d.isValid()) {
      return d.format(format);
    } else {
      return String(dateInput ?? '');
    }
  }

  /** Checks if a date is the same or after another date. */
  static isSameOrAfter(
    date: dayjs.ConfigType,
    compareDate: dayjs.ConfigType,
    unit?: dayjs.ManipulateType,
  ) {
    return dayjs(date).isSameOrAfter(dayjs(compareDate), unit);
  }

  /** Checks if a date is the same or before another date. */
  static isSameOrBefore(
    date: dayjs.ConfigType,
    compareDate: dayjs.ConfigType,
    unit?: dayjs.ManipulateType,
  ) {
    return dayjs(date).isSameOrBefore(dayjs(compareDate), unit);
  }

  /** Checks if two dates are the same. */
  static isSame(date: dayjs.ConfigType, compareDate: dayjs.ConfigType) {
    return dayjs(date).isSame(dayjs(compareDate));
  }

  static isSameDay(date: dayjs.ConfigType, compareDate: dayjs.ConfigType) {
    return dayjs(date).isSame(dayjs(compareDate), 'day');
  }

  /** Checks if a date is before another date. */
  static isBefore(
    date: dayjs.ConfigType,
    compareDate: dayjs.ConfigType,
    unit?: dayjs.ManipulateType,
  ) {
    return dayjs(date).isBefore(dayjs(compareDate), unit);
  }

  /** Checks if a date is after another date. */
  static isAfter(
    date: dayjs.ConfigType,
    compareDate: dayjs.ConfigType,
    unit?: dayjs.ManipulateType,
  ) {
    return dayjs(date).isAfter(dayjs(compareDate), unit);
  }

  /** Adds a specified amount of time to a date. */
  static add(
    date: dayjs.ConfigType,
    amount: number,
    unit: dayjs.ManipulateType,
  ) {
    return dayjs(date).add(amount, unit);
  }

  /** Subtracts a specified amount of time from a date. */
  static subtract(
    date: dayjs.ConfigType,
    amount: number,
    unit: dayjs.ManipulateType,
  ) {
    return dayjs(date).subtract(amount, unit);
  }

  /** Checks if a date is valid. */
  static isValid(date: dayjs.ConfigType) {
    return dayjs(date).isValid();
  }

  /** Finds the difference between two dates in the specified unit. */
  static diff(
    date: dayjs.ConfigType,
    compareDate: dayjs.ConfigType,
    unit: dayjs.ManipulateType = 'day',
  ) {
    return dayjs(date).diff(dayjs(compareDate), unit);
  }

  static diffInCalendarDays(date1: dayjs.ConfigType, date2: dayjs.ConfigType) {
    return dayjs(date1).startOf('day').diff(dayjs(date2).startOf('day'), 'day');
  }

  /** Gets the full month name of the given date. */
  static getMonthName(date: dayjs.ConfigType) {
    return dayjs(date).localeData().months(dayjs(date));
  }

  /** Returns the year part of a date. */
  static getYear(date: dayjs.ConfigType) {
    return dayjs(date).year();
  }

  /** Returns the month (0-based) part of a date. */
  static getMonth(date: dayjs.ConfigType) {
    return dayjs(date).month();
  }

  /** Returns the day of the month. */
  static getDayOfMonth(date: dayjs.ConfigType) {
    return dayjs(date).date();
  }

  /** Returns the start of the day for a given date. */
  static startOfDay(date: dayjs.ConfigType) {
    return dayjs(date).startOf('day').tz(this.getUserTimeZone());
  }

  /** Returns the end of the day for a given date. */
  static endOfDay(date: dayjs.ConfigType) {
    return dayjs(date).endOf('day').tz(this.getUserTimeZone());
  }

  /** Returns the start of the month for a given date. */
  static startOfMonth(date: dayjs.ConfigType) {
    return dayjs(date).startOf('month');
  }

  /** Returns the end of the month for a given date. */
  static endOfMonth(date: dayjs.ConfigType) {
    return dayjs(date).endOf('month');
  }

  /** Returns the start of the week for a given date. */
  static startOfWeek(date: dayjs.ConfigType) {
    return dayjs(date).startOf('week');
  }

  /** Returns the end of the week for a given date. */
  static endOfWeek(date: dayjs.ConfigType) {
    return dayjs(date).endOf('week');
  }

  /** Checks if two dates are in the same week. */
  static isSameWeek(date1: dayjs.ConfigType, date2: dayjs.ConfigType) {
    return dayjs(date1).isSame(dayjs(date2), 'week');
  }

  /** Converts a date to its string representation. */
  static toString(date: dayjs.ConfigType): string {
    return dayjs(date).toString();
  }

  /** Converts a date to its ISO 8601 string representation. */
  static toISOString(date: dayjs.ConfigType): string {
    return dayjs(date).toISOString();
  }

  /** Converts a date to a native JavaScript Date object. */
  static toDate(date: dayjs.ConfigType): Date {
    return dayjs(date).toDate();
  }

  /** Returns the Unix timestamp (in seconds) of a date. */
  static toUnix(date: dayjs.ConfigType): number {
    return dayjs(date).unix();
  }

  /** Converts a Unix timestamp (in seconds) to a dayjs object. */
  static fromUnix(unix: number): dayjs.Dayjs {
    return dayjs.unix(unix);
  }

  /** Checks if a given date is in a leap year. */
  static isLeapYear(date: dayjs.ConfigType): boolean {
    return dayjs(date).isLeapYear();
  }

  /** Returns true if the given date is today. */
  static isToday(date: dayjs.ConfigType): boolean {
    return dayjs(date).isSame(dayjs(), 'day');
  }

  /** Returns true if the given date is yesterday. */
  static isYesterday(date: dayjs.ConfigType): boolean {
    return dayjs(date).isSame(dayjs().subtract(1, 'day'), 'day');
  }

  /** Returns true if the given date is tomorrow. */
  static isTomorrow(date: dayjs.ConfigType): boolean {
    return dayjs(date).isSame(dayjs().add(1, 'day'), 'day');
  }

  /** Returns the day of the week (0 for Sunday, 6 for Saturday). */
  static getDayOfWeek(date: dayjs.ConfigType): number {
    return dayjs(date).day();
  }

  /** Returns the number of days in the month of the given date. */
  static getDaysInMonth(date: dayjs.ConfigType): number {
    return dayjs(date).daysInMonth();
  }

  /** Returns a human-readable relative time string (e.g., "3 days ago", "in 2 hours"). */
  static fromNow(date: dayjs.ConfigType, fallback: string = ''): string {
    if (!dayjs(date).isValid()) return fallback;
    return dayjs(date).fromNow();
  }

  /** Returns a human-readable relative time string from one date to another. */
  static fromTo(fromDate: dayjs.ConfigType, toDate: dayjs.ConfigType): string {
    return dayjs(fromDate).from(dayjs(toDate));
  }

  /** Converts a date to UTC. */
  static toUTC(date: dayjs.ConfigType): dayjs.Dayjs {
    return dayjs(date).utc();
  }

  /** Converts a date to the specified IANA timezone (e.g., 'Asia/Kolkata'). */
  static toTimezone(date: dayjs.ConfigType, tz: string): dayjs.Dayjs {
    return dayjs(date).tz(tz);
  }

  /** Gets the current time in the specified IANA timezone. */
  static nowInTimezone(
    tz: string | undefined = this.getUserTimeZone(),
  ): dayjs.Dayjs {
    return dayjs().tz(tz);
  }

  /** Returns the timezone offset in minutes for a given date (UTC offset). */
  static getTimezoneOffset(date: dayjs.ConfigType): number {
    return dayjs(date).utcOffset();
  }

  /** Returns the current user's timezone as an IANA string (e.g., 'Asia/Kolkata'). */
  static getUserTimeZone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  /**
   * Calculates the time remaining between two dates.
   * Returns absolute time difference and whether it's negative (i.e., fromDate is after toDate).
   */
  static calculateTimeRemaining = (
    fromDate: Date | Dayjs | string,
    toDate: Date | Dayjs | string,
  ) => {
    const start = dayjs(fromDate);
    const end = dayjs(toDate);

    if (!start.isValid() || !end.isValid()) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, isNegative: false };
    }

    const diffMs = end.diff(start);
    const isNegative = diffMs < 0;
    const diffDuration = dayjs.duration(Math.abs(diffMs));

    return {
      days: Math.floor(diffDuration.asDays()),
      hours: diffDuration.hours(),
      minutes: diffDuration.minutes(),
      seconds: diffDuration.seconds(),
      isNegative,
    };
  };

  /** Returns an ISO string with time set to the start of the day (00:00:00). */
  static getIsoWithTimeAsStart(date: dayjs.ConfigType): string {
    return dayjs(date)?.toISOString()?.replace(/T.*Z/, 'T00:00:00Z') || '';
  }
}

export default DateTime;
